import { Component, OnDestroy, OnInit } from '@angular/core';
import { EnvironmentName, SituViewWebApiClient, IfcParseError, ListPagedHomeIfcFilesRequest, EnvironmentSettings, IfcFile, ProductName } from 'situview-webapi-client';
import { Subject, Subscription } from 'rxjs';
import { AuthService, BreadcrumbService, StateService } from 'src/app/services';
import { PageEvent } from '@angular/material/paginator';
import { Company } from 'identity-webapi-client';

@Component({
  selector: 'app-ifc-report',
  templateUrl: './ifc-report.component.html',
  styleUrls: ['./ifc-report.component.less']
})
export class IfcReportComponent implements OnInit, OnDestroy {
  EnvironmentName = EnvironmentName;
  environments: EnvironmentSettings[] = [];
  environmentIndexes: EnvironmentCutOffIndex[] = [];

  selectedCompany: Company | undefined;

  errorPageNumber = 1;
  errorPageSize = 5;
  ifcErrors: IfcParseError[] = [];
  totalErrors: number = 0;

  ifcFileGroups: IfcFileGroup[] = [];
  pageNumber = 1;
  pageSize = 250;
  totalItems: number = 0;

  search: string = '';
  searchFilter: Subject<string> = new Subject<string>();

  
  private _showLoadingBar = false;
  public get showLoadingBar(): boolean {
    return this._showLoadingBar;
  }

  public set showLoadingBar(val: boolean) {
    this._showLoadingBar = val;
    this.breadcrumbService.setIsLoading(val);
  }

  private _loadingUpdates: boolean = false;
  public get loadingUpdates(): boolean {
    return this._loadingUpdates;
  }
  public set loadingUpdates(val: boolean) {
    this._loadingUpdates = val;
    this.showLoadingBar = this._loadingUpdates || this._loadingErrors;
  }

  private _loadingErrors: boolean = false;
  public get loadingErrors(): boolean {
    return this._loadingErrors;
  }
  public set loadingErrors(val: boolean) {
    this._loadingErrors = val;
    this.showLoadingBar = this._loadingUpdates || this._loadingErrors;
  }

  private _subscriptions: Subscription[] = [];

  constructor(
    private situViewClient: SituViewWebApiClient,
    private breadcrumbService: BreadcrumbService,
    private stateService: StateService
  ) {}

  ngOnInit(): void {
    let loadEnvironments = true;

    this._subscriptions.push(this.stateService.state$.subscribe(x => {
      if (x.company?.id !== this.selectedCompany?.id) {
        this.selectedCompany = x.company ?? undefined;
        this.loadIfcReportData(loadEnvironments);
        this.loadIfcErrorsData();
      }
    }));
  }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      for (let sub of this._subscriptions) {
        sub.unsubscribe();
      }
      this._subscriptions = [];
    }
  }

  onPageChange(evt: PageEvent) {
    this.pageSize = evt.pageSize;
    this.pageNumber = evt.pageIndex + 1;
    this.loadIfcReportData();
  }

  onErrorsPageChange(evt: PageEvent) {
    this.errorPageSize = evt.pageSize;
    this.errorPageNumber = evt.pageIndex + 1;
    this.loadIfcErrorsData();
  }

  loadIfcReportData(loadEnvironments: boolean = false) {
    this.loadingUpdates = true;

    var request = new ListPagedHomeIfcFilesRequest();
    request.builder = this.selectedCompany?.code;
    request.pageNumber = this.pageNumber;
    request.pageSize = this.pageSize;
    request.includeEnvironments = loadEnvironments;

    this.situViewClient.listPagedHomeIfcFiles(request).then(response => {
      if (!!response.environmentSettings?.length) {
        this.environments = response.environmentSettings;
      }

      this.ifcFileGroups = this.groupIfcFiles(response.items ?? []);
      this.totalItems = response.totalItems ?? 0;

      this.loadingUpdates = false;
    }, error => {
      this.loadingUpdates = false;
    });
  }

  loadIfcErrorsData() {
    this.loadingErrors = true;

    var request = new ListPagedHomeIfcFilesRequest();
    request.builder = this.selectedCompany?.code;
    request.pageNumber = this.errorPageNumber;
    request.pageSize = this.errorPageSize;

    this.situViewClient.listPagedIfcFileErrors(request).then(response => {
      if (response.success) {
        this.ifcErrors = response.items ?? [];
        this.totalErrors = response.totalItems ?? 0;
      }
      else {
        this.ifcErrors = [];
      }
      this.loadingErrors = false;
    }, error => {
      this.loadingErrors = false;
    });
  }

  groupIfcFiles(ifcFiles: IfcFile[]) {
    let groupedByDateObject = ifcFiles.reduce((accumulator: Record<string, IfcFile[]>, ifcFile: IfcFile) => {
      let date = this.getIfcFileGroupDate(ifcFile);
      const key = date.getTime().toString();

      accumulator[key] = [...accumulator[key] ?? [], ifcFile];
      return accumulator;
    }, {});

    let ifcFileGroups = Object.keys(groupedByDateObject).map((key) => {
      let maxDate = new Date(Math.max(...(groupedByDateObject[key] as IfcFile[]).map(x => x.dateAdded?.getTime() ?? 0)));
      let minDate = new Date(Math.min(...(groupedByDateObject[key] as IfcFile[]).map(x => x.dateAdded?.getTime() ?? 0)));

      let groupedByHome = (groupedByDateObject[key] as IfcFile[]).reduce((accumulator: Record<string, IfcFile[]>, ifcFile) => {
        accumulator[ifcFile?.home ?? ''] = [...accumulator[ifcFile?.home ?? ''] || [], ifcFile];
        return accumulator;
      }, {});

      let ifcFileGroup: IfcFileGroup = {
        maxDate: maxDate,
        minDate: minDate,
        ifcFileGroupDetails: (Object.keys(groupedByHome)).map((home) => {
          return {
            home: home,
            files: groupedByHome[home]
          } as IfcFileGroupDetails;
        })
      };
      return ifcFileGroup;
    });

    this.setEnvironmentIndexes(ifcFileGroups);

    ifcFileGroups.forEach(ifcFileGroup => {
      ifcFileGroup.environmentSettings = this.getEnvironments(ifcFileGroups, ifcFileGroup);
    });

    return ifcFileGroups;
  }

  getIfcFileGroupDate(ifcFile: IfcFile): Date {
    let date = new Date(ifcFile.dateAdded as Date);
    date.setHours(0, 0, 0, 0);

    let environmentsOnSameDay = this.environments
      .filter(x => !!x.defaultCuttoffDate && x.defaultCuttoffDate.toDateString() == ifcFile.dateAdded?.toDateString());
    if (!environmentsOnSameDay?.length) return date;

    let environmentsInsideOf = environmentsOnSameDay.filter(x => x.defaultCuttoffDate as Date >= (ifcFile.dateAdded as Date));
    if (!environmentsInsideOf?.length) return date;

    let nearestEnvironmentCutOffDate = Math.min(...environmentsInsideOf.map(x => (x.defaultCuttoffDate as Date).getTime()));
    if (!nearestEnvironmentCutOffDate || nearestEnvironmentCutOffDate === Infinity) return date;

    return new Date(nearestEnvironmentCutOffDate);
  }

  setEnvironmentIndexes(ifcFileGroups: IfcFileGroup[]) {
    let indexes: EnvironmentCutOffIndex[] = [];
    this.environments.forEach(environment => {
      if (!environment.defaultCuttoffDate) {
        indexes.push({ index: 0, environmentSettings: environment });
        return;
      }

      let fileGroupsNotIncluded = ifcFileGroups.filter(x => (environment.defaultCuttoffDate as Date) < (x.maxDate as Date));
      if (!fileGroupsNotIncluded.length) {
        indexes.push({ index: 0, environmentSettings: environment });
        return;
      }

      let lastFileGroupDateIncluded = Math.max(...ifcFileGroups.filter(x => (environment.defaultCuttoffDate as Date) >= (x.maxDate as Date)).map((x) => (x.maxDate as Date).getTime()));
      let index = ifcFileGroups.findIndex(x => x.maxDate?.getTime() == lastFileGroupDateIncluded);
      indexes.push({ index: index, environmentSettings: environment });
      return;
    });

    this.environmentIndexes = indexes;
  }

  getEnvironments(ifcFileGroups: IfcFileGroup[], ifcFileGroup: IfcFileGroup) {
    const index = ifcFileGroups.indexOf(ifcFileGroup);
    var environments = this.environmentIndexes?.filter(x => x.index == index)
      .map(x => x.environmentSettings)
      .sort((a, b) => {
        if (!a.defaultCuttoffDate && b.defaultCuttoffDate) {
          return -1;
        }
        if (!a.defaultCuttoffDate && !b.defaultCuttoffDate) {
          let environmentA = a.environment?.environment ?? EnvironmentName.Production;
          let environmentB = b.environment?.environment ?? EnvironmentName.Production;
          return environmentA - environmentB;
        }
        if (a.defaultCuttoffDate && !b.defaultCuttoffDate) {
          return 1;
        }

        return (a.defaultCuttoffDate as Date).getTime() - (b.defaultCuttoffDate as Date).getTime();
      });

    let groupedEnvironments: GroupedEnvironmentSetting[] = this.getGroupedEnvironmentSettings(environments);

    return groupedEnvironments;
  }

  getGroupedEnvironmentSettings(environments: EnvironmentSettings[]) {
    var helper: any = {};
    const result = environments.reduce((r: any[], o) => {
      let product = this.getProductName(o);
      let environmentName = this.getEnvironmentName(o);

      const key = (o.defaultCuttoffDate?.toISOString() || 'N/A') + '-' + environmentName;

      if (!helper[key]) {
        let newGroupedItem: GroupedEnvironmentSetting = new GroupedEnvironmentSetting(o.defaultCuttoffDate)

        if (product) newGroupedItem.products.push(product);
        if (environmentName) newGroupedItem.environments.push(environmentName);
        helper[key] = newGroupedItem;

        r.push(helper[key]);
      } else {
        if (!helper[key].products.includes(product)) {
          helper[key].products.push(product);
        }
        if (!helper[key].environments.includes(environmentName)) {
          helper[key].environments.push(environmentName);
        }
      }

      return r;
    }, []);

    return result;
  }

  getEnvironmentName(environment: EnvironmentSettings) {
    if (!environment?.environment?.environment) {
      return null;
    }

    let environmentName = EnvironmentName[environment.environment.environment];
    return environmentName;
  }

  getProductName(environment: EnvironmentSettings) {
    if (!environment?.environment?.product) {
      return null;
    }

    let productName = ProductName[environment.environment.product];
    return productName;
  }
}

interface EnvironmentCutOffIndex {
  index: Number; //ifcFileGroup index to display before
  environmentSettings: EnvironmentSettings;
}

interface IfcFileGroup {
  environmentSettings?: GroupedEnvironmentSetting[];
  maxDate?: Date;
  minDate?: Date;
  ifcFileGroupDetails?: IfcFileGroupDetails[] | undefined;
}
interface IfcFileGroupDetails {
  home?: string | undefined;
  files?: IfcFile[] | undefined;
}

class GroupedEnvironmentSetting {
  defaultCuttoffDate?: Date | undefined;
  products: string[];
  environments: string[];

  constructor(defaultCuttoffDate?: Date) {
    this.defaultCuttoffDate = defaultCuttoffDate;
    this.products = [];
    this.environments = [];
  }

  getProductsString() {
    return this.products.join(', ');
  }

  getDistinctEnvironmentsList() {
    return this.environments.join(', ');
  }
}
